/**
 * Blog System Types
 * 
 * This file defines the structure for blog posts.
 * To create a new blog post, add a numbered file in blog/posts/
 * following the BlogPost interface and register it in blog/posts/index.ts.
 */

export interface BlogPostMeta {
  /** Unique slug for the URL (e.g., "introducing-sqwish") */
  slug: string;
  /** Post title */
  title: string;
  /** Optional title lines for the post detail header */
  titleLines?: string[];
  /** Short description/excerpt for the listing page */
  excerpt: string;
  /** Publication date in ISO format (YYYY-MM-DD) */
  date: string;
  /** Author name(s) - optional */
  authors?: string[];
  /** Optional collaboration note (e.g., "in collaboration with OpenAI") */
  collaboration?: string;
  /** Tags/categories for filtering */
  tags: string[];
  /** Reading time in minutes (calculated or manual) */
  readingTime?: number;
  /** Optional cover image path */
  coverImage?: string;
  /** Whether the post is published (draft = false) */
  published: boolean;
}

export type KetteiEmbedId =
  | 'kettei-trace'
  | 'kettei-timeline'
  | 'kettei-audits'
  | 'kettei-family'
  | 'kettei-benchmarks'
  | 'kettei-radar'
  | 'kettei-games'
  | 'kettei-latency'
  | 'kettei-calibration'
  | 'kettei-cascade'
  | 'kettei-tuning'
  | 'kettei-loop';

export type BlogEmbedId = KetteiEmbedId
  | 'router-frontier'
  | 'weather-map'
  | 'routing-question-contrast'
  | 'layer-routability'
  | 'model-fingerprints'
  | 'router-architecture'
  | 'router-playground'
  | 'synthetic-loop';

export interface BlogSectionToc {
  /** Optional side-menu label; defaults to heading text when omitted */
  label?: string;
  /** Side-menu visual hierarchy */
  level?: 2 | 3;
  /** Side-menu entry style */
  kind?: 'section' | 'demo';
}

export interface BlogSection {
  /** Section type */
  type: 'paragraph' | 'heading' | 'code' | 'image' | 'quote' | 'list' | 'callout' | 'math' | 'html' | 'embed';
  /** Content varies by type */
  content: string;
  /** Optional stable anchor id for headings, figures, demos, or custom sections */
  id?: string;
  /** Optional side-menu metadata; set false to suppress an otherwise indexable heading */
  toc?: BlogSectionToc | false;
  /** Optional: heading level (2-4) for heading type */
  level?: 2 | 3 | 4;
  /** Optional: language for code blocks */
  language?: string;
  /** Optional: image alt text */
  alt?: string;
  /** Optional: image caption */
  caption?: string;
  /** Optional: image source for light theme; falls back to content */
  lightContent?: string;
  /** Optional: image source for dark theme */
  darkContent?: string;
  /** Optional image max width in pixels */
  imageMaxWidth?: number;
  /** Optional image max height in pixels */
  imageMaxHeight?: number;
  /** Optional: list items for list type */
  items?: string[];
  /** Optional: ordered vs unordered list */
  ordered?: boolean;
  /** Optional: callout variant */
  variant?: 'info' | 'warning' | 'tip' | 'note';
  /** Optional controlled React-rendered article block */
  embed?: BlogEmbedId;
}

export interface BlogPost extends BlogPostMeta {
  /** Post content as structured sections */
  content: BlogSection[];
}

export interface BlogPostPreview extends BlogPostMeta {
  /** Preview doesn't include full content */
}

/**
 * Helper to format date for display
 */
export function formatDate(dateString: string): string {
  const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString);
  const date = dateOnlyMatch
    ? new Date(
        Number(dateOnlyMatch[1]),
        Number(dateOnlyMatch[2]) - 1,
        Number(dateOnlyMatch[3])
      )
    : new Date(dateString);

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Helper to calculate reading time from content
 */
export function calculateReadingTime(content: BlogSection[]): number {
  const wordsPerMinute = 200;
  let totalWords = 0;
  
  for (const section of content) {
    if (section.type === 'paragraph' || section.type === 'heading' || section.type === 'quote') {
      totalWords += section.content.split(/\s+/).length;
    } else if (section.type === 'list' && section.items) {
      totalWords += section.items.join(' ').split(/\s+/).length;
    }
  }
  
  return Math.max(1, Math.ceil(totalWords / wordsPerMinute));
}
